import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Draggable } from "@hello-pangea/dnd";
import CardLoader from "./CardLoader";
import { useState } from "react";

export function KanbanCard({ item, children, className, style, ...props }) {
  const { draggableId, kanban_row_index } = item;
  return (
    <Draggable draggableId={draggableId} index={kanban_row_index}>
      {(provided) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          {...props}
          className={cn(
            "p-4 mb-2 cursor-pointer hover:shadow-md transition-shadow",
            className
          )}
          style={{ ...provided.draggableProps.style, ...style }}
        >
          {children}
        </Card>
      )}
    </Draggable>
  );
}

export function KanbanBoard({
  children,
  statusColumns,
  className,
  updateKanban,
  renderCreateCard,
  isLoading,
}) {
  // Extract all KanbanCards from children
  const cards = children?.map((child) => child.props.item);
  const [dragging, setDragging] = useState(false);
  const handleDragEnd = (result) => {
    setDragging(false);
    const { source, destination, draggableId } = result;
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    // Find the moved card
    const movedCard = cards.find((card) => card.draggableId === draggableId);
    if (!movedCard) return;

    // Create a copy of cards without the moved card
    const updatedCards = cards.filter(
      (card) => card.draggableId !== draggableId
    );

    // Update the moved card's status and index
    movedCard.status = destination.droppableId;

    // Get all cards in destination column (excluding the moved card)
    const destinationColumnCards = updatedCards.filter(
      (card) => card.status === destination.droppableId
    );

    // Insert the moved card at the new position
    destinationColumnCards.splice(destination.index, 0, movedCard);

    // Update the kanban_row_index values for all cards in the destination column
    destinationColumnCards.forEach((card, index) => {
      card.kanban_row_index = index;
    });

    // Also update indices in the source column if it's different from destination
    if (source.droppableId !== destination.droppableId) {
      const sourceColumnCards = updatedCards.filter(
        (card) => card.status === source.droppableId
      );
      sourceColumnCards.forEach((card, index) => {
        card.kanban_row_index = index;
      });
    }

    // Combine all cards again
    const finalCards = [
      ...updatedCards.filter(
        (card) =>
          card.status !== destination.droppableId &&
          card.status !== source.droppableId
      ),
      ...(source.droppableId !== destination.droppableId
        ? updatedCards.filter((card) => card.status === source.droppableId)
        : []),
      ...destinationColumnCards,
    ];

    // Update the kanban board
    updateKanban(finalCards);
  };

  const getColor = (color) => (color ? color : `var(--kanban-column-gray)`);

  return (
    <div className={cn("p-6 space-y-8", className)}>
      <DragDropContext
        onDragStart={() => setDragging(true)}
        onDragEnd={handleDragEnd}
      >
        <div
          className={`grid gap-4`}
          style={{
            gridTemplateColumns: `repeat(${statusColumns.length}, minmax(0, 1fr))`,
          }}
        >
          {statusColumns.map((column) => (
            <div key={column.id} className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">{column.title}</h3>
                <Badge
                  variant="secondary"
                  style={{
                    backgroundColor: getColor(column?.color),
                  }}
                >
                  {cards?.filter((card) => card.status === column.id).length}
                </Badge>
              </div>
              {isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <CardLoader key={index} compact />
                  ))}
                </div>
              ) : (
                children && (
                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={cn(
                          "flex-1 overflow-y-auto rounded-lg p-2 border"
                        )}
                        style={{
                          backgroundColor: getColor(column?.columnColor),
                          opacity: snapshot.isDraggingOver ? 0.9 : 1,
                        }}
                      >
                        {children
                          ?.sort(
                            ({ props: a }, { props: b }) =>
                              a.item.kanban_row_index - b.item.kanban_row_index
                          )
                          .filter(
                            ({ props }) => props.item.status === column.id
                          )}
                        {renderCreateCard &&
                          renderCreateCard({ status: column.id })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )
              )}
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}
